import { useState } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { menu, routes } from './routes';
import { useGetSetupStatusQuery } from './api/auth/AuthApi';
import { useGetShopSettingQuery } from './api/billing/BillApi';
import { useThemeMode } from './providers/ThemeModeProvider';
import AuthPage from './components/AuthPage';
import Masters from './components/Master/Masters';
import ProductList from './components/Product/ProductList';
import Dashboard from './components/Dashboard/Dashboard';
import Billing from './components/Billing/Billing';
import BillList from './components/Billing/BillList';
import PurchaseView from './components/Purchase/PurchaseView';
import Outstanding from './components/Outstanding/Outstanding';
import Reports from './components/Reports/Reports';
import Settings from './components/Settings/Settings';
import { can, currentUser, signOut } from './utils/auth';
import { readable } from './utils/text';

export default function App() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { mode, toggleMode } = useThemeMode();
  const [user, setUser] = useState(currentUser());
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);
  const { data: status, isLoading, refetch } = useGetSetupStatusQuery(undefined, { skip: !!user });
  const { data: shop } = useGetShopSettingQuery(undefined, { skip: !user });

  if (!user) {
    if (isLoading) {
      return (
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      );
    }
    // One page for both: a brand-new shop signs up and names itself, everyone
    // else signs in. The shop's own name comes back from the public status call.
    return (
      <AuthPage
        setupNeeded={!!status?.setupNeeded}
        shopName={status?.shopName}
        onSignedIn={() => {
          setUser(currentUser());
          refetch();
        }}
      />
    );
  }

  // The menu is built from the owner's permission grid, not from the role, so a
  // change there changes the menu at this person's next login.
  const allowed = menu.filter((item) => can(item.screen, 'view', user));
  const landing = allowed[0]?.path ?? routes.billing;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{ bgcolor: mode === 'dark' ? '#0a0f16' : '#001529', color: '#fff' }}
      >
        <Toolbar sx={{ gap: 0.5, flexWrap: 'wrap', minHeight: 60 }}>
          {allowed.map((item) => {
            const active = pathname.startsWith(item.path);
            return (
              <Button
                key={item.path}
                onClick={() => navigate(item.path)}
                sx={{
                  color: active ? '#fff' : 'rgba(255,255,255,.72)',
                  fontWeight: active ? 700 : 400,
                  borderBottom: active ? '2px solid' : '2px solid transparent',
                  borderColor: active ? 'primary.main' : 'transparent',
                  borderRadius: 0,
                  px: 1.5,
                  '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,.06)' },
                }}
              >
                {item.label}
              </Button>
            );
          })}

          <Box flexGrow={1} />

          <Tooltip title={`Switch to ${mode === 'dark' ? 'light' : 'dark'} mode (Ctrl + M)`}>
            <IconButton onClick={toggleMode} sx={{ color: mode === 'dark' ? '#ffd700' : 'rgba(255,255,255,.85)' }}>
              {mode === 'dark' ? <WbSunnyOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          <Button
            onClick={(event) => setProfileAnchor(event.currentTarget)}
            sx={{ color: '#fff', textTransform: 'none', gap: 1 }}
          >
            <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', fontSize: '.8rem' }}>
              {(user.fullName || user.userName).charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ textAlign: 'left', display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2" sx={{ lineHeight: 1.1 }}>
                {user.fullName || user.userName}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,.6)' }}>
                {readable(user.roleName || user.role)}
              </Typography>
            </Box>
          </Button>

          <Menu
            anchorEl={profileAnchor}
            open={!!profileAnchor}
            onClose={() => setProfileAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { width: 240, mt: 1 } } }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {user.fullName || user.userName}
              </Typography>
              <Chip size="small" label={readable(user.roleName || user.role)} sx={{ mt: 0.5 }} />
            </Box>
            <Divider />
            <MenuItem
              onClick={() => {
                setProfileAnchor(null);
                toggleMode();
              }}
            >
              {mode === 'dark' ? <WbSunnyOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
              <Typography sx={{ ml: 1.5 }}>{mode === 'dark' ? 'Light mode' : 'Dark mode'}</Typography>
            </MenuItem>
            <MenuItem onClick={signOut}>
              <LogoutIcon fontSize="small" />
              <Typography sx={{ ml: 1.5 }}>Sign out</Typography>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: { xs: 2, md: 3 }, flexGrow: 1 }}>
        <Routes>
          <Route path="/" element={<Navigate to={landing} replace />} />
          {can('REPORTS', 'view', user) && <Route path={routes.dashboard} element={<Dashboard />} />}
          {/* Billing opens on the list of bills with a New Bill button; the counter
              screen is what that button opens. Both paths land on the same screen so
              an old link still works. */}
          {can('BILLING', 'view', user) && <Route path={routes.billing} element={<BillList />} />}
          {can('BILLS', 'view', user) && <Route path={routes.bills} element={<BillList />} />}
          {can('MASTERS', 'view', user) && <Route path={routes.products} element={<ProductList />} />}
          {can('MASTERS', 'view', user) && <Route path={routes.masters} element={<Masters />} />}
          {can('PURCHASE', 'view', user) && <Route path={routes.purchase} element={<PurchaseView />} />}
          {can('OUTSTANDING', 'view', user) && <Route path={routes.outstanding} element={<Outstanding />} />}
          {can('REPORTS', 'view', user) && <Route path={routes.reports} element={<Reports />} />}
          {can('SETTINGS', 'view', user) && <Route path={routes.settings} element={<Settings />} />}
          <Route path="*" element={<Navigate to={landing} replace />} />
        </Routes>
      </Box>

      {/* The year is read from the clock, not typed in - nobody remembers to edit a
          footer on 1 January, and a stale one is the first thing a customer notices. */}
      <Box sx={{ textAlign: 'center', py: 1, fontSize: 13, color: 'text.secondary' }}>
        &copy; {new Date().getFullYear()} {String(shop?.shopName || 'Mobile Shop')}
      </Box>
    </Box>
  );
}
